import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { CallStatusType } from "../../../configs";
import { AudioConferenceCall } from "../../../models";
import { SendOutboundDtmfRequestModel } from "../../../models/api/Call";
import { formatUSPhoneNumber } from "../../../utils/phoneNumberFormatting";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  callSid?: string | null;
  selectedCall?: AudioConferenceCall | null;
  onSendOutboundDtmf: (value: SendOutboundDtmfRequestModel) => Promise<any>;
};

const keypadRows = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

function getCallStatusLabel(current?: CallStatusType, prev?: CallStatusType): string {
  if (!current) return "Waiting";

  switch (current) {
    case CallStatusType.OutboundCallInitiated:
      return "Initiated";
    case CallStatusType.OutboundCallRinging:
      return "Ringing";
    case CallStatusType.OutboundCallReceived:
      return "In-call";
    case CallStatusType.OutboundCallCompleted:
      if (prev === CallStatusType.OutboundCallReceived) return "Hung Up";
      if (prev === CallStatusType.OutboundCallNotAnswered) return "Not Answered";
      return "Completed";
    case CallStatusType.OutboundCallNotAnswered:
      return "Not Answered";
    case CallStatusType.OutboundCallNotReceived:
      return "Voice Mail";
    default:
      return "Waiting";
  }
}

export function ModalOutboundDtmfDial({
  show,
  handleShow,
  callSid,
  selectedCall,
  onSendOutboundDtmf,
}: Props) {
  const [codeInput, setCodeInput] = useState("");
  const [legCallSidInput, setLegCallSidInput] = useState("");
  const [lastAutoPopulatedLegCallSid, setLastAutoPopulatedLegCallSid] = useState("");
  const [autoSentLength, setAutoSentLength] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [lastSentDigits, setLastSentDigits] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");

  const canSendDigits =
    !!callSid &&
    !!selectedCall?.callSid &&
    selectedCall.status?.current === CallStatusType.OutboundCallReceived;

  useEffect(() => {
    if (!show) {
      setCodeInput("");
      setLegCallSidInput("");
      setLastAutoPopulatedLegCallSid("");
      setAutoSentLength(0);
      setIsSending(false);
      setLastSentDigits("");
      setFeedbackMessage("");
    }
  }, [show]);

  useEffect(() => {
    const nextLegCallSid = selectedCall?.callSid ?? "";

    if (!nextLegCallSid) {
      return;
    }

    if (!legCallSidInput || legCallSidInput === lastAutoPopulatedLegCallSid) {
      setLegCallSidInput(nextLegCallSid);
      setLastAutoPopulatedLegCallSid(nextLegCallSid);
    }
  }, [lastAutoPopulatedLegCallSid, legCallSidInput, selectedCall?.callSid]);

  useEffect(() => {
    setAutoSentLength((prev) => Math.min(prev, codeInput.length));
  }, [codeInput.length]);

  useEffect(() => {
    if (
      !show ||
      codeInput.length <= autoSentLength ||
      !legCallSidInput ||
      !callSid ||
      !canSendDigits ||
      isSending
    ) {
      return;
    }

    const digitsToSend = codeInput.slice(autoSentLength);
    const nextAutoSentLength = codeInput.length;
    const timer = window.setTimeout(async () => {
      setIsSending(true);
      setFeedbackMessage(`Sending ${digitsToSend}`);

      try {
        await onSendOutboundDtmf({
          callSid,
          legCallSid: legCallSidInput,
          digits: digitsToSend,
        });

        setLastSentDigits(digitsToSend);
        setFeedbackMessage(`Sent ${digitsToSend}`);
        setAutoSentLength(nextAutoSentLength);
      } catch (error) {
        console.error(error);
        setFeedbackMessage(`Failed to send ${digitsToSend}`);
      } finally {
        setIsSending(false);
      }
    }, 1500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    callSid,
    canSendDigits,
    codeInput,
    autoSentLength,
    isSending,
    legCallSidInput,
    onSendOutboundDtmf,
    show,
  ]);

  const appendKey = (digit: string) => {
    setFeedbackMessage("");
    setCodeInput((prev) => prev + digit);
  };

  const triggerApiManually = async () => {
    if (!callSid) {
      setFeedbackMessage("Parent call SID is missing.");
      return;
    }

    if (!legCallSidInput) {
      setFeedbackMessage("legCallSid is required.");
      return;
    }

    if (!codeInput) {
      setFeedbackMessage("Code input is required.");
      return;
    }

    setIsSending(true);
    setFeedbackMessage(`Triggering API with ${codeInput}`);

    try {
      await onSendOutboundDtmf({
        callSid,
        legCallSid: legCallSidInput,
        digits: codeInput,
      });
      setLastSentDigits(codeInput);
      setFeedbackMessage(`Triggered API with ${codeInput}`);
    } catch (error) {
      console.error(error);
      setFeedbackMessage(`Failed to trigger API with ${codeInput}`);
    } finally {
      setIsSending(false);
    }
  };

  const callStatusLabel = getCallStatusLabel(selectedCall?.status?.current, selectedCall?.status?.prev);
  const targetNumberLabel =
    formatUSPhoneNumber(selectedCall?.calledNumber || "") ||
    selectedCall?.calledNumber ||
    "";

  return (
    <Modal show={show} onHide={() => handleShow(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Keypad</Modal.Title>
      </Modal.Header>

      <Modal.Body className="dialer-modal-body">
        <div className="dialer-modal">
          <div className="d-flex flex-column justify-content-center align-items-center dialer-content">
            <div className="w-100 mb-3" style={{ maxWidth: "320px" }}>
              <div className="d-flex align-items-end mb-2" style={{ gap: "8px" }}>
                <div style={{ flex: "1 1 0" }}>
                  <label style={{ fontSize: "11px", fontWeight: 600, marginBottom: "4px", display: "block" }}>
                    callSid
                  </label>
                  <input
                    className="form-control form-control-sm"
                    value={callSid ?? ""}
                    readOnly
                    style={{ height: "32px" }}
                  />
                </div>
                <div style={{ flex: "1 1 0" }}>
                  <label style={{ fontSize: "11px", fontWeight: 600, marginBottom: "4px", display: "block" }}>
                    Code
                  </label>
                  <input
                    className="form-control form-control-sm"
                    value={codeInput}
                    onChange={(event) => {
                      setFeedbackMessage("");
                      setCodeInput(event.target.value);
                    }}
                    placeholder="Code"
                    style={{ height: "32px" }}
                  />
                </div>
              </div>
              <div className="d-flex align-items-end" style={{ gap: "8px" }}>
                <div style={{ flex: "1 1 auto" }}>
                  <label style={{ fontSize: "11px", fontWeight: 600, marginBottom: "4px", display: "block" }}>
                    legCallSid
                  </label>
                  <input
                    className="form-control form-control-sm"
                    value={legCallSidInput}
                    onChange={(event) => {
                      setFeedbackMessage("");
                      setLegCallSidInput(event.target.value);
                    }}
                    placeholder="Editable leg call SID"
                    style={{ height: "32px" }}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={triggerApiManually}
                  disabled={isSending}
                  style={{ height: "32px", minWidth: "104px", padding: "0 12px", whiteSpace: "nowrap" }}
                >
                  Trigger API
                </button>
              </div>
            </div>

            <div className="d-flex flex-row">
              <div
                className="dialer-output shadow-sm"
                style={{
                  minWidth: codeInput.length > 25 ? codeInput.length * 10 + "px" : "250px",
                }}
              >
                <input
                  className="border form-control text-center"
                  value={codeInput}
                  readOnly
                  placeholder="Enter code"
                  style={{
                    minWidth: codeInput.length > 25 ? codeInput.length * 10 + "px" : "250px",
                  }}
                />
              </div>
            </div>

            <div className="key-row-wrapper mt-3">
              {keypadRows.map((row) => (
                <div className="key-row" key={row.join("-")}>
                  {row.map((digit) => (
                    <button
                      key={digit}
                      value={digit}
                      className="btn btn-light shadow-sm ripple"
                      type="button"
                      onClick={() => appendKey(digit)}
                    >
                      {digit}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            <div className="text-center mt-3" style={{ maxWidth: "260px", fontSize: "12px" }}>
              <div style={{ fontWeight: 600 }}>{targetNumberLabel || "Selected call"}</div>
              <div style={{ color: canSendDigits ? "#198754" : "#fd7e14" }}>{callStatusLabel}</div>
              <div className="mt-2" style={{ color: "#6c757d" }}>
                Digits send automatically 1.5 seconds after typing stops.
              </div>
              {!callSid && <div style={{ color: "#dc3545" }}>Parent call SID is missing.</div>}
              {!selectedCall && (
                <div style={{ color: "#fd7e14" }}>No active call was selected.</div>
              )}
              {!legCallSidInput && selectedCall && !selectedCall.callSid && (
                <div style={{ color: "#fd7e14" }}>Waiting for outbound leg SID.</div>
              )}
              {selectedCall && !canSendDigits && selectedCall.status?.current !== CallStatusType.OutboundCallReceived && (
                <div style={{ color: "#fd7e14" }}>
                  Digits will stay buffered until the outbound call is connected.
                </div>
              )}
              {isSending && <div style={{ color: "#0d6efd" }}>Sending digits...</div>}
              {!isSending && feedbackMessage && <div>{feedbackMessage}</div>}
              {!isSending && lastSentDigits && <div>Last sent: {lastSentDigits}</div>}
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
